import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { useCallback, useEffect, useState } from 'react';
import { apiOrigin, apiUrl } from './apiBase.ts';
import {
  parseReleaseManifest,
  type ReleaseManifest,
  resolveReleaseUrl,
  shouldOfferUpdate,
} from './appUpdate.ts';
import { recordSwallowed } from './swallowed.ts';

const DISMISSED_KEY = 'pbfork.update.dismissed';
const CHECK_INTERVAL_MS = 24 * 60 * 60 * 1000;

/**
 * `sha256` manifestte bilgilendirme amaçlıdır: istemci APK'yı indirip
 * doğrulamaz, kurulumu Android'in imza denetimi korur. Yayın betiği manifest
 * ile APK'nın özetini karşılaştırır (bkz. `ops/debian/99-upgrade.sh`).
 */

interface PbforkShellPlugin {
  openExternal(options: { url: string }): Promise<void>;
}

const shellPlugin = Capacitor.isNativePlatform()
  ? registerPlugin<PbforkShellPlugin>('PbforkShell')
  : null;

export interface AppUpdateState {
  manifest: ReleaseManifest | null;
  open: () => Promise<void>;
  dismiss: () => void;
}

/**
 * Native APK sürümünü `/releases/latest.json` ile karşılaştırır.
 * Web'de no-op; mevcut PWA/service worker davranışı değişmez.
 */
export function useAppUpdate(): AppUpdateState {
  const [manifest, setManifest] = useState<ReleaseManifest | null>(null);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    const check = async (): Promise<void> => {
      try {
        const info = await App.getInfo();
        const response = await fetch(apiUrl('/releases/latest.json'), {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;

        const latest = parseReleaseManifest(await response.json());
        if (!shouldOfferUpdate(info.build, latest, window.sessionStorage.getItem(DISMISSED_KEY))) {
          return;
        }
        if (!cancelled && latest !== null) setManifest(latest);
      } catch (error) {
        recordSwallowed('useAppUpdate', error);
        // Güncelleme kontrolü sessizce başarısız olabilir; oynatmayı engellemez.
      }
    };

    void check();
    const timer = window.setInterval(() => void check(), CHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const open = useCallback(async (): Promise<void> => {
    if (manifest === null) return;
    const url = resolveReleaseUrl(manifest.url, apiOrigin());
    if (url === null) return;

    // Custom Tab bazı MIUI/Chrome sürümlerinde APK indirmesini güvenlik
    // gerekçesiyle engelliyor. Yeni APK'larda normal tarayıcıyı kullan.
    if (shellPlugin !== null && Capacitor.isPluginAvailable('PbforkShell')) {
      await shellPlugin.openExternal({ url }).catch(() => undefined);
      return;
    }
    await Browser.open({ url }).catch(() => undefined);
  }, [manifest]);

  const dismiss = useCallback((): void => {
    if (manifest !== null) {
      try {
        window.sessionStorage.setItem(DISMISSED_KEY, String(manifest.versionCode));
      } catch (error) {
        recordSwallowed('useAppUpdate', error);
        // Gizli mod / depolama kapalı: yalnız bu oturum için gizlenir.
      }
    }
    setManifest(null);
  }, [manifest]);

  return { manifest, open, dismiss };
}
